/**
 * @fileoverview The things you can do to a photo that are not editing its details.
 *
 * Saving the date and the words is the dialog's own business — it owns
 * those fields. Downloading, replacing, moving and deleting are four
 * self-contained errands with nothing to do with each other beyond happening
 * to the same picture, and none of them needs to be written out among the
 * JSX.
 *
 * A failure is said in the server's words where it explained a refusal (the
 * file is too large, it is not an image), and in ours otherwise
 * (errorMessage).
 *
 * Where: EditPhotoDialog.
 * API: GET …/:photoId/file (download), PUT /photos/:plantId/:photoId/plant
 * (move, plants only), PUT …/:photoId/file (replace), DELETE …/:photoId;
 * … is /photos/:plantId or /locations/:id/photos.
 * Settings: none.
 * Words (en/es): plants.photo.downloadFailed, plants.photo.replaceFailed;
 * the dialog says the others.
 *
 * @module usePhotoActions
 */

import { useCallback, useState } from 'react';
import { fetchPhotoBlobFor, movePhotoToPlant, replacePhotoFileFor, deletePhotoFor } from '@/shared/api/photos';
import type { Subject } from '@/shared/api/subject';
import { PlantPhoto } from '@/shared/types/photo';
import { calendarDay } from '@/shared/utils/dateUtils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { debugLog } from '@/config/config';
import { errorMessage } from '@/shared/lib/errorMessage';
import { PHOTO_DOWNLOAD_FILE_PREFIX, PHOTO_DOWNLOAD_REVOKE_DELAY_MS } from '@/config/tunables/plantList';

/** How the dialog hears that an errand changed the photo under it. */
interface PhotoActionCallbacks {
  /** The photo is no longer on this plant — deleted, or moved away. */
  onGone: (photoId: number) => void;
  /** The picture changed but the photo did not. */
  onReplaced: (photo: PlantPhoto) => void;
}

/** The four errands, each with a flag that is true while it runs. */
export interface PhotoActions {
  /** Saves the full-size picture to the device. */
  download: () => Promise<void>;
  /** True while the download runs. */
  isDownloading: boolean;
  /** Files the photo on another plant. Rethrows, so the caller can stay open. */
  move: (targetPlantObjId: number) => Promise<void>;
  /** True while the move runs. */
  isMoving: boolean;
  /** Puts a different picture in this photo's place. Rethrows on failure. */
  replace: (file: File) => Promise<void>;
  /** True while the replacement uploads. */
  isReplacing: boolean;
  /** Deletes the photo for good. Rethrows on failure. */
  remove: () => Promise<void>;
  /** True while the delete runs. */
  isRemoving: boolean;
  /** Whatever went wrong last, in the reader's language, or null. */
  error: string | null;
  /** Forgets it. */
  clearError: () => void;
}

/**
 * Download, move, replace and delete for one photo.
 *
 * @param subject - The plant or location the photo belongs to now.
 * @param photo - The photo, or undefined before one is chosen.
 * @param callbacks - How the card is told what happened.
 * @returns The four errands, their flags and the last error.
 */
export function usePhotoActions(
  subject: Subject,
  photo: PlantPhoto | undefined,
  { onGone, onReplaced }: PhotoActionCallbacks
): PhotoActions {
  const { reactTranslate: t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const photoId = photo?.photo_id ?? 0;

  /**
   * Saves the picture to the device.
   *
   * The bytes come from our own API rather than the signed bucket URL: the
   * bucket sends no CORS headers, so the browser will paint that URL into an
   * <img> and refuse to hand it to script. A signed link in an <a download>
   * would open the photo in a tab instead of saving it, which is not what the
   * button says it does.
   *
   * Named for the day it was taken, because "IMG_4471.jpg" in a downloads
   * folder is a photo of nothing.
   */
  const download = useCallback(async (): Promise<void> => {
    if (!photo) return;

    setError(null);
    setIsDownloading(true);
    let href: string | null = null;
    try {
      const blob = await fetchPhotoBlobFor(subject, photoId);
      href = URL.createObjectURL(blob);

      const day = calendarDay(photo.date_taken).toFormat('yyyy-LL-dd');
      const link = document.createElement('a');
      link.href = href;
      link.download = PHOTO_DOWNLOAD_FILE_PREFIX + day + '-' + photoId + '.jpg';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      debugLog('PhotoUploadDialog', 'Could not download the photo:', err);
      setError(errorMessage(err, t('plants.photo.downloadFailed')));
    } finally {
      // Long enough for the browser to have started reading it. Revoking
      // immediately after click() cancels the save in some browsers.
      if (href) setTimeout(() => URL.revokeObjectURL(href as string), PHOTO_DOWNLOAD_REVOKE_DELAY_MS);
      setIsDownloading(false);
    }
  }, [photo, subject, photoId, t]);

  /**
   * Moves the photo onto another plant.
   *
   * Reported as gone, because from this card's point of view it is: the card
   * keeps its own copy of the photo list and this picture is no longer in it.
   * A failure is thrown, not shown here: the caller (EditPhotoDialog's save)
   * says it once, under the words, where it said it twice before.
   */
  const move = useCallback(async (targetPlantObjId: number): Promise<void> => {
    setError(null);
    setIsMoving(true);
    try {
      await movePhotoToPlant(subject.id, photoId, targetPlantObjId);
      onGone(photoId);
    } catch (err) {
      debugLog('PhotoUploadDialog', 'Could not move the photo:', err);
      throw err;
    } finally {
      setIsMoving(false);
    }
  }, [subject, photoId, onGone]);

  /**
   * Swaps in a different picture, keeping the photo it belongs to.
   *
   * The server's reply speaks only of the picture, so the caller merges it
   * over the photo it already has rather than replacing it wholesale.
   */
  const replace = useCallback(async (file: File): Promise<void> => {
    if (!photo) return;

    setError(null);
    setIsReplacing(true);
    try {
      const updated = await replacePhotoFileFor(subject, photoId, file);
      onReplaced({ ...photo, ...updated });
    } catch (err) {
      debugLog('PhotoUploadDialog', 'Could not replace the photo:', err);
      setError(errorMessage(err, t('plants.photo.replaceFailed')));
      throw err;
    } finally {
      setIsReplacing(false);
    }
  }, [photo, subject, photoId, onReplaced, t]);

  /**
   * Deletes the photo. Unlike the others it sets no error of its own: the
   * question is asked in a panel over the dialog, where a line in the dialog
   * would not be seen, so a failure is rethrown for the caller to report
   * (EditPhotoDialog says it in a toast).
   */
  const remove = useCallback(async (): Promise<void> => {
    if (!photo) return;

    setIsRemoving(true);
    try {
      await deletePhotoFor(subject, photoId);
      onGone(photoId);
    } catch (err) {
      debugLog('PhotoUploadDialog', 'Delete error:', err);
      throw err;
    } finally {
      setIsRemoving(false);
    }
  }, [photo, subject, photoId, onGone]);

  /** Forgets the last failure, as starting another errand should. */
  const clearError = useCallback(() => setError(null), []);

  return {
    download, isDownloading,
    move, isMoving,
    replace, isReplacing,
    remove, isRemoving,
    error, clearError,
  };
}
